import WifSelect from '@/components/WifSelect'
import { MaterialInput } from '@/components/utilities/MaterialInput'
import SimplifiedLayout from '@/layouts/SimplifiedLayout'
import { RegisterSchema } from '@/lib/schema/RegisterFormSchema'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React from 'react'
import { CustomerErrorCode, CustomerUserError, useRegisterUserMutation } from 'shopify/api'
import phoneCode from '../../lib/json/phoneCode.json'

const Register: React.FC<{ from: string }> = ({
  from,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { t } = useTranslation('register')
  const router = useRouter()
  const [tncChecked, setTncChecked] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string>('')
  const [countryCode, setCountryCode] = React.useState<string>('+62')

  const [registerAccount] = useRegisterUserMutation()

  const onRegister = async () => {
    setIsLoading(true)
    setErrorMessage('')
    try {
      const { data } = await registerAccount({
        variables: {
          input: {
            firstName: formik.values.name,
            lastName: formik.values.companyName,
            phone: `${countryCode}${formik.values.phone}`,
            email: formik.values.email,
            password: formik.values.password,
          },
        },
      })

      const responseErrors = data?.customerCreate?.customerUserErrors as CustomerUserError[]

      if (!responseErrors || responseErrors.length === 0)
        router.push(`/register/done?email=${data?.customerCreate?.customer?.email}`)

      if (
        responseErrors.length > 0 &&
        responseErrors[0].code === CustomerErrorCode.Invalid &&
        responseErrors[0].field &&
        responseErrors[0].field[1] === 'phone'
      ) {
        setErrorMessage('register:Error.Phone_Format')
      }

      if (
        responseErrors.length > 0 &&
        responseErrors[0].code === CustomerErrorCode.Taken &&
        responseErrors[0].field &&
        responseErrors[0].field[1] === 'email'
      ) {
        setErrorMessage('register:Error.Email_Taken')
      }

      if (
        responseErrors.length > 0 &&
        responseErrors[0].code === CustomerErrorCode.Taken &&
        responseErrors[0].field &&
        responseErrors[0].field[1] === 'phone'
      ) {
        setErrorMessage('register:Error.Phone_Taken')
      }

      //wording login

      setIsLoading(false)
    } catch (err: any) {
      if (err.message) setErrorMessage(err.message)
      setIsLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      companyName: '',
      email: '',
      phone: '',
      password: '',
    },
    onSubmit: onRegister,
    validationSchema: RegisterSchema(countryCode.length),
  })

  return (
    <SimplifiedLayout browserTitle='WifKain - Register' title='Register' from={from}>
      <div className='pt-[60px] pb-[70px] px-4 bg-white min-h-screen'>
        <form className='mt-8' onSubmit={formik.handleSubmit} data-test='register-form'>
          <MaterialInput
            name='name'
            label={t('register:Field_Name.Name')}
            type='text'
            value={formik.values.name}
            error={!!formik.errors?.name && !!formik.touched?.name}
            errorMessage={t(formik.errors.name!)}
            icon='/icons/ic-user.svg'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <MaterialInput
            name='companyName'
            label={t('register:Field_Name.Company')}
            type='text'
            value={formik.values.companyName}
            error={!!formik.errors?.companyName && !!formik.touched?.companyName}
            errorMessage={t(formik.errors.companyName!)}
            icon='/icons/ic-briefcase.svg'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <MaterialInput
            name='email'
            label='Email'
            type='email'
            value={formik.values.email}
            error={!!formik.errors?.email && !!formik.touched?.email}
            errorMessage={t(formik.errors.email!)}
            icon='/icons/ic-mail.svg'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <MaterialInput
            name='phone'
            label={t('register:Field_Name.Phone')}
            type='text'
            value={formik.values.phone}
            error={!!formik.errors?.phone && !!formik.touched?.phone}
            errorMessage={t(formik.errors.phone!)}
            icon='/icons/ic-mobile.svg'
            onChange={(e) => {
              const regex = /^[0-9\b]+$/
              if (e.target.value === '' || regex.test(e.target.value)) {
                formik.setFieldValue('phone', `${e.target.value}`)
              }
            }}
            onFocus={() => {}}
            onBlur={formik.handleBlur}
            children={
              <WifSelect
                name='countryCode'
                options={phoneCode.countries.map(({ code, name }) => ({
                  name: `${code}`,
                  value: code,
                }))}
                error={false}
                onBlur={() => {}}
                onChange={(e) => setCountryCode(e.target.value)}
                value={countryCode}
                customInputClass='!border-transparent !h-5 !w-auto max-w-[90px] text-sm font-bold'
                rightIcon={
                  <Image src='/icons/ic-chevron-down.svg' alt='Open logo' width={16} height={16} />
                }
              />
            }
            autoFocus
          />

          <MaterialInput
            name='password'
            label='Password'
            type='password'
            value={formik.values.password}
            error={!!formik.errors?.password && !!formik.touched?.password}
            errorMessage={t(formik.errors.password!)}
            icon='/icons/ic-key.svg'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <div className='flex items-center gap-4 mb-12'>
            <input
              id='check[TnC]'
              type='checkbox'
              className={clsx(
                'appearance-none w-[25px] h-[25px] border border-[#ECECEC] rounded-[5px] ml-4 cursor-pointer transition duration-150',
                "bg-[url('/icons/ic-check.svg')] bg-no-repeat bg-center",
                'checked:bg-primary checked:border-primary'
              )}
              checked={tncChecked}
              onChange={() => setTncChecked(!tncChecked)}
              data-test='input-checkbox-tnc'
            />
            <label htmlFor='check[TnC]'>
              <span dangerouslySetInnerHTML={{ __html: t('register:Tnc_String') }} />
            </label>
          </div>

          <button
            type='submit'
            className={clsx(
              'bg-primary text-white font-bold text-sm py-[18px] w-full rounded-[10px]',
              'shadow-[0px_4px_14px_0px_rgba(27,71,130,0.19)]',
              'disabled:bg-[#F3F3F3] disabled:shadow-[0px_2px_10px_0px_rgba(183,183,183,0.2)] disabled:text-[#676767]'
            )}
            disabled={!(formik.isValid && formik.dirty && tncChecked) || isLoading}
            data-test='register-button-submit'
          >
            {t('common:Word_Register')}
          </button>
          {!!errorMessage && (
            <span className='mt-1 text-error-red text-xs font-semibold'>
              {t(errorMessage, { interpolation: { alwaysFormat: true } })}
            </span>
          )}

          <div className='flex items-center w-full justify-center gap-6 mt-16'>
            <span>{t('register:Have_Account')}</span>
            <a href='/login' className='text-wif-orange font-bold' data-test='register-link-login'>
              Login
            </a>
          </div>
        </form>
      </div>
    </SimplifiedLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ locale, req }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale!, ['register', 'navbar', 'common'])),
      from: req.headers.referer || '',
    },
  }
}

export default Register
